'use client';

import { useCallback, useSyncExternalStore } from 'react';
import {
    VEHICLE_DEFAULT_PER_PAGE,
    VEHICLE_PAGE_PARAM,
    VEHICLE_PER_PAGE_OPTIONS,
    VEHICLE_PER_PAGE_PARAM,
    type VehiclePerPage,
} from './vehicleListing.constants';

/** pushState fires no event of its own, so writes announce themselves. */
const URL_CHANGE_EVENT = 'vehicle-listing:url';

function subscribe(onChange: () => void) {
    window.addEventListener('popstate', onChange);
    window.addEventListener(URL_CHANGE_EVENT, onChange);

    return () => {
        window.removeEventListener('popstate', onChange);
        window.removeEventListener(URL_CHANGE_EVENT, onChange);
    };
}

const readSearch = () => window.location.search;

// The server renders page one; a deep link takes over right after hydration.
const readServerSearch = () => '';

function parsePage(value: string | null): number {
    const page = Number(value);
    return Number.isInteger(page) && page > 0 ? page : 1;
}

function parsePerPage(value: string | null): VehiclePerPage {
    const perPage = Number(value) as VehiclePerPage;
    return VEHICLE_PER_PAGE_OPTIONS.includes(perPage) ? perPage : VEHICLE_DEFAULT_PER_PAGE;
}

function writeParams(page: number, perPage: VehiclePerPage) {
    const url = new URL(window.location.href);

    if (page > 1) url.searchParams.set(VEHICLE_PAGE_PARAM, String(page));
    else url.searchParams.delete(VEHICLE_PAGE_PARAM);

    if (perPage !== VEHICLE_DEFAULT_PER_PAGE) url.searchParams.set(VEHICLE_PER_PAGE_PARAM, String(perPage));
    else url.searchParams.delete(VEHICLE_PER_PAGE_PARAM);

    if (url.href === window.location.href) return;

    window.history.pushState(null, '', url.href);
    window.dispatchEvent(new Event(URL_CHANGE_EVENT));
}

/** Page and page size live in the URL (`?seite=2&pro_seite=24`), so back and forward work. */
export function useVehiclePageParams() {
    const search = useSyncExternalStore(subscribe, readSearch, readServerSearch);
    const params = new URLSearchParams(search);
    const page = parsePage(params.get(VEHICLE_PAGE_PARAM));
    const perPage = parsePerPage(params.get(VEHICLE_PER_PAGE_PARAM));

    const setPage = useCallback((next: number) => writeParams(next, perPage), [perPage]);
    const setPerPage = useCallback((next: VehiclePerPage) => writeParams(1, next), []);

    return { page, perPage, setPage, setPerPage };
}
